import { i18n } from "@app/i18n";
import { viewAsHeaders } from "@app/services/view-as";
import useUser from "@app/zustand/user";

/**
 * Sent by a request the page makes on its own, such as a poll: the API does not count it as activity,
 * so an unattended page still lets the session lapse (PT-171).
 */
export const BACKGROUND_REQUEST_HEADERS: Record<string, string> = {
  "X-Background-Request": "1",
};

interface ApiArgs {
  path?: string;
  query?: Record<
    string,
    string | string[] | number | boolean | null | undefined
  >;
  headers?: Record<string, string>;
  body?: Record<string, unknown>;
}

interface ApiExecuteArgs extends ApiArgs {
  method?: string;
}

class ApiService {
  private getBaseUrl(): string {
    // In production, always use relative URLs to avoid mixed content issues
    // Nginx will proxy /api/* requests to the backend
    if (import.meta.env.PROD) {
      return "";
    }

    // In development, use VITE_API_URL or fallback to localhost
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      console.warn(
        "VITE_API_URL is not set. Defaulting to http://localhost:2222. Please set it in your .env file.",
      );
      return "http://localhost:2222";
    }

    // Force HTTPS in production if an absolute URL is provided (shouldn't happen, but just in case)
    if (apiUrl.startsWith("http://")) {
      console.warn(
        "VITE_API_URL uses HTTP. Converting to HTTPS to avoid mixed content errors.",
      );
      return apiUrl.replace("http://", "https://");
    }

    return apiUrl;
  }

  getUrl = (
    path: string,
    query: Record<
      string,
      string | number | boolean | string[] | undefined | null
    > = {},
  ) => {
    const baseUrl = this.getBaseUrl();
    // If using relative URLs (empty baseUrl), prepend /api for nginx proxying
    const apiPath = baseUrl === "" ? `/api${path}` : path;

    const searchParams = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";
    return `${baseUrl}${apiPath}${queryString}`;
  };
  execute = async ({
    method = "GET",
    path = "",
    query = {},
    headers = {},
    body,
  }: ApiExecuteArgs) => {
    try {
      const url = this.getUrl(path, query);
      // « Voir comme » — the eye menu and the speciality picker. The rule lives in ONE module
      // (``services/view-as``) because the raw ``fetch()`` calls on /configuration need the very
      // same headers, and the two copies had already drifted apart.
      const headersForPreview = viewAsHeaders();
      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Accept-Language": i18n.locale || "fr",
          ...headersForPreview,
          ...headers,
        },
        body: body ? JSON.stringify(body) : null,
      });

      if (response.status === 401) {
        useUser.getState().endSession();
        return { ok: false, error: "Session expirée", status: 401 };
      }

      const contentType = response.headers.get("Content-Type") ?? "";

      if (contentType.includes("application/xml")) {
        try {
          const readableRes = await response.text();
          return readableRes;
        } catch (e) {
          console.error("ERROR IN RESPONSE XML", response);
          console.error(e);
        }
      }

      // Sur 5xx ou réponse non-JSON, ne pas tenter response.json() pour éviter SyntaxError
      const isJson = contentType.includes("application/json");
      if (!isJson && (response.status >= 500 || response.status >= 400)) {
        const text = await response.text();
        console.error(
          "Réponse serveur non-JSON",
          url,
          response.status,
          text.slice(0, 200),
        );
        return {
          ok: false,
          error:
            response.status >= 500
              ? "Erreur interne du serveur. Veuillez réessayer ou contacter l'équipe si le problème persiste."
              : "Erreur de la requête.",
          data: null,
        };
      }

      if (response) {
        try {
          const readableRes = await response.json();
          return readableRes;
        } catch (e) {
          console.error("ERROR IN RESPONSE JSON", url, e);
          return {
            ok: false,
            error:
              "Erreur de parsing JSON (le serveur a peut-être renvoyé une erreur HTML ou texte).",
            originalError: e,
          };
        }
      }

      return response;
    } catch (e) {
      console.error("error in api", e);
      return {
        ok: false,
        error:
          "Veuillez nous excuser, cette erreur est inattendue. Veuillez retenter dans quelques instants ou nous contacter si l'erreur persiste.",
      };
    }
  };

  get = async (args: ApiArgs) => this.execute({ method: "GET", ...args });
  post = async (args: ApiArgs) => this.execute({ method: "POST", ...args });
  put = async (args: ApiArgs) => this.execute({ method: "PUT", ...args });
  patch = async (args: ApiArgs) => this.execute({ method: "PATCH", ...args });
  delete = async (args: ApiArgs) => this.execute({ method: "DELETE", ...args });
}

const API = new ApiService();
export default API;
